import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../../lib/query-keys';
import { automationRequest } from '../client';
import type {
  AccountChanges,
  AccountCheck,
  CheckAllState,
  CleanupPreview,
  CleanupResult,
  PaymentSetJob,
  PoolAccountSummary,
  RegistrationAccount,
  RegistrationKind,
  RegistrationList,
  TestPaymentResult,
  TopUpResult,
} from '../testrun-types';

/**
 * TestRun calls of the accounts pages only (TestRun src/api/http.ts, server
 * src/index.mjs). The shared reads and repair/recheck/unspend are in hooks.ts.
 *
 * Phones, IINs and BINs always travel in the request body, never in a URL:
 * the ingress logs URLs. Operations on the stand (payments, top-up, main
 * account, cleanup) change nothing TestRun stores, so they invalidate nothing;
 * the page re-checks the account when the user asks for fresh balances.
 */

const enc = encodeURIComponent;

const invalidateTestRun = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: queryKeys.automation.testRun() });

/* ─── request and response shapes not in testrun-types.ts ─── */

/** Who to log in as on the stand. */
export interface AccountCredentials {
  phone: string;
  iin: string;
  bin?: string;
}

export interface AccountCheckInput extends AccountCredentials {
  /** log in anew (OTP and biometry), do not take the remembered token */
  fresh?: boolean;
}

/** A transfer between the account's own accounts; without ids — 10 ₸ from the main one. */
export interface TransferInput extends AccountCredentials {
  fromId?: string;
  toId?: string;
  amount?: number;
}

export interface SetMainResult {
  main: { id: string; name: string; number: string | null } | null;
  changed: { name: string };
  removed: boolean;
}

export interface OtpResult {
  code: string | null;
  at?: string | null;
}

export interface ImportRow {
  phone: string;
  iin: string;
  bin?: string;
  label?: string;
  line?: number;
}

export interface ImportResult {
  added: number;
  updated: number;
  invalid: { line: number; reason: string }[];
  checkStarted: boolean;
  checkAll: CheckAllState;
}

export interface RegistrationImportRow extends ImportRow {
  kind: RegistrationKind;
}

export interface RegistrationImportResult {
  added: number;
  updated: number;
  pooled: number;
  invalid: { line: number; reason: string }[];
}

const post = <T>(queryClient: QueryClient, path: string, body?: unknown) =>
  automationRequest<T>(queryClient, path, {
    method: 'POST',
    ...(body === undefined ? {} : { body }),
  });

/* ─── the pool ─── */

/** Logs into the account on the stand (OTP and biometry when needed) — up to half a minute. */
export const useCheckAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AccountCheckInput) =>
      post<AccountCheck & { poolId?: string | null }>(queryClient, '/accounts/check', input),
    onSuccess: () => invalidateTestRun(queryClient),
  });
};

/** Re-checks the whole pool in the background; the progress comes with the pool list. */
export const useCheckAllPool = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => post<CheckAllState>(queryClient, '/accounts/check-all'),
    onSuccess: () => invalidateTestRun(queryClient),
  });
};

/** «phone; IIN; BIN; label» rows into the pool; TestRun checks them in the background. */
export const useImportAccounts = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rows: ImportRow[]) =>
      post<ImportResult>(queryClient, '/accounts/import', { rows }),
    onSuccess: () => invalidateTestRun(queryClient),
  });
};

export const useUpdatePoolAccount = (accountId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: { label: string }) =>
      automationRequest<PoolAccountSummary>(queryClient, `/accounts/${enc(accountId)}`, {
        method: 'PATCH',
        body: patch,
      }),
    onSuccess: () => invalidateTestRun(queryClient),
  });
};

/** Takes the account out of the pool; nothing happens to it on the stand. */
export const useDeletePoolAccount = (accountId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      automationRequest<{ ok: true }>(queryClient, `/accounts/${enc(accountId)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: queryKeys.automation.testRun('pool', accountId) });
      void invalidateTestRun(queryClient);
    },
  });
};

/**
 * The night watch log. Polled: while «check all» runs the log grows every few
 * seconds, otherwise the night pass may land while the page is open.
 */
export const useAccountChangesLog = (days: number, poolId: string | undefined, live: boolean) => {
  const queryClient = useQueryClient();
  const query = new URLSearchParams({ days: String(days), ...(poolId ? { poolId } : {}) });
  return useQuery({
    queryKey: queryKeys.automation.testRun('changes', String(days), poolId ?? ''),
    queryFn: ({ signal }) =>
      automationRequest<AccountChanges>(queryClient, `/accounts/changes?${query}`, { signal }),
    refetchInterval: live ? 3_000 : 60_000,
  });
};

/* ─── operations on the stand: real, signed with an OTP to the account's phone ─── */

export const useTestPayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TransferInput) =>
      post<TestPaymentResult>(queryClient, '/accounts/test-payment', input),
  });
};

/** One transfer per call from the richest own account to the payer account, up to `target`. */
export const useTopUp = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AccountCredentials & { target: number }) =>
      post<TopUpResult>(queryClient, '/accounts/top-up', input),
  });
};

/** Makes an account the main one (`remove` takes the mark off). No OTP. */
export const useSetMainAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AccountCredentials & { accountId: string; remove?: boolean }) =>
      post<SetMainResult>(queryClient, '/accounts/set-main', input),
  });
};

/** Six test payments of different kinds, in the background; progress — `usePaymentSet`. */
export const useStartPaymentSet = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AccountCredentials) =>
      post<PaymentSetJob>(queryClient, '/accounts/payment-set', input),
  });
};

export const usePaymentSet = (jobId: string | null) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: queryKeys.automation.testRun('payment-set', jobId ?? ''),
    queryFn: ({ signal }) =>
      automationRequest<PaymentSetJob>(queryClient, `/accounts/payment-set/${enc(jobId ?? '')}`, {
        signal,
      }),
    enabled: Boolean(jobId),
    refetchInterval: (query) => (query.state.data?.finishedAt ? false : 2_000),
  });
};

/** Unsigned payments and drafts of the account — changes nothing. */
export const useCleanupPreview = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AccountCredentials) =>
      post<CleanupPreview>(queryClient, '/accounts/cleanup/preview', input),
  });
};

/** Cancels and deletes exactly the ids passed (the ones the preview showed). */
export const useCleanup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AccountCredentials & { paymentIds: string[]; draftIds: string[] }) =>
      post<CleanupResult>(queryClient, '/accounts/cleanup', input),
  });
};

/** The last OTP sent to the phone in 10 minutes (from Kafdrop). The phone goes in the body. */
const latestOtp = (queryClient: QueryClient, phone: string, signal?: AbortSignal) =>
  automationRequest<OtpResult>(queryClient, '/accounts/otp', {
    method: 'POST',
    body: { phone },
    ...(signal ? { signal } : {}),
  });

export const useLatestOtp = () => {
  const queryClient = useQueryClient();
  return useMutation({ mutationFn: (phone: string) => latestOtp(queryClient, phone) });
};

/** The OTP tab: asks again every 5 seconds for three minutes after the user asked. */
export const OTP_WATCH_MS = 3 * 60_000;

/** `watching` — still within the three minutes after the user asked. */
export const useOtpWatch = (asked: { phone: string; at: number } | null, watching: boolean) => {
  const queryClient = useQueryClient();
  return useQuery({
    // keyed by the moment asked, so the phone stays out of the cache keys
    queryKey: queryKeys.automation.testRun('otp', String(asked?.at ?? '')),
    queryFn: ({ signal }) => latestOtp(queryClient, asked?.phone ?? '', signal),
    enabled: asked !== null,
    retry: false,
    refetchInterval: watching ? 5_000 : false,
  });
};

/* ─── «for registration» ─── */

/** While some are issued to a run, TestRun settles them after it — the list is watched. */
export const useRegistrationList = () => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: queryKeys.automation.testRun('registration'),
    queryFn: ({ signal }) =>
      automationRequest<RegistrationList>(queryClient, '/registration-accounts', { signal }),
    refetchInterval: (query) =>
      query.state.data?.accounts.some((a) => a.status === 'issued') ? 30_000 : false,
  });
};

export const useImportRegistration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rows: RegistrationImportRow[]) =>
      post<RegistrationImportResult>(queryClient, '/registration-accounts/import', { rows }),
    onSuccess: () => invalidateTestRun(queryClient),
  });
};

/** authenticate without an OTP (no SMS goes out): registered or not; a registered one goes to the pool. */
export const useCheckRegistration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      post<RegistrationAccount>(queryClient, `/registration-accounts/${enc(id)}/check`),
    onSuccess: () => invalidateTestRun(queryClient),
  });
};

/** Issue it again: registration with this account did not go through. */
export const useReleaseRegistration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      post<RegistrationAccount>(queryClient, `/registration-accounts/${enc(id)}/release`),
    onSuccess: () => invalidateTestRun(queryClient),
  });
};

export const useDeleteRegistration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      automationRequest<{ ok: true }>(queryClient, `/registration-accounts/${enc(id)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => invalidateTestRun(queryClient),
  });
};

/** Check the issued ones whose run has finished (the list also does it by itself). */
export const useSettleRegistration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      post<{ running: boolean; checked?: number }>(queryClient, '/registration-accounts/settle'),
    onSuccess: () => invalidateTestRun(queryClient),
  });
};
